import { useCallback, useEffect, useRef, useState } from 'react'
import { ExploreWidgetConfig } from '../../../config'
import {
  MAX_PROBE_PAGES,
  PAGE_SIZE,
  byName,
  parseRows,
  propertyNamesUrl,
} from '../../../../../common/guest/name-finder'

// Fetch/parse core moved to common/guest/name-finder (#1138) so
// property-finder's hook can share it; re-exported to keep existing imports
// (WhatPopover, propertyNav, ProWhereField, tests) stable.
export type { NameFinderMatch } from '../../../../../common/guest/name-finder'
import type { NameFinderMatch } from '../../../../../common/guest/name-finder'

/**
 * Find-by-name property lookup (#853, toolkit: SBP name finder).
 *
 * SHIPS DARK: the Central endpoint is central#2885 (filed, unbuilt). The hook
 * probes once per mount on first use; any non-OK/malformed response marks the
 * feature unavailable and the UI hides it silently — enabling the setting on a
 * fleet without the endpoint must never surface a broken UI. When the endpoint
 * lands, matches light up with no widget change (path below tracks the #2885
 * contract: name/slug rows, paginated, ?q= server filtering).
 *
 * Threshold semantics (toolkit parity, adapted): the toolkit's server decides
 * full-vs-query mode; widget-side we page the unfiltered list until either
 * exhaustion (≤ threshold → cache it, match client-side) or passing the
 * threshold (→ query mode: debounced, cancellable ?q= lookups). The threshold
 * is RETAINED because the browser only holds fetched pages, never the catalog.
 */

type Mode = 'unavailable' | 'probing' | 'full' | 'query'

export function useNameFinder(
  feedId: number,
  config: ExploreWidgetConfig['searchbar']['nameFinder'],
  active: boolean,
) {
  const [mode, setMode] = useState<Mode>('probing')
  const [matches, setMatches] = useState<NameFinderMatch[]>([])
  const fullListRef = useRef<NameFinderMatch[] | null>(null)
  const probedRef = useRef(false)
  const reqSeqRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const probeAbortRef = useRef<AbortController | null>(null)

  // One probe per mount, on first activation. Exhaustively pages the list up
  // to the threshold; unavailable on ANY failure — silence over breakage.
  // The probe is aborted on UNMOUNT only (see the cleanup effect below), not
  // when `active` flips off, so a close mid-probe doesn't strand the mount in
  // 'probing' forever — but a discarded mobile sheet stops fetching.
  useEffect(() => {
    if (!active || !config.enabled || probedRef.current) return
    probedRef.current = true
    const ac = new AbortController()
    probeAbortRef.current = ac
    ;(async () => {
      try {
        const collected: NameFinderMatch[] = []
        let exhausted = false
        for (let page = 1; page <= MAX_PROBE_PAGES; page++) {
          if (ac.signal.aborted) return
          const res = await fetch(propertyNamesUrl(feedId, { page: String(page), limit: String(PAGE_SIZE) }), { signal: ac.signal })
          if (!res.ok) throw new Error(`property-names ${res.status}`)
          const rows = parseRows(await res.json())
          if (rows === null) throw new Error('property-names malformed payload')
          collected.push(...rows)
          if (collected.length > config.serverLookupThreshold) {
            if (!ac.signal.aborted) setMode('query')
            return
          }
          if (rows.length < PAGE_SIZE) {
            exhausted = true
            break
          }
        }
        if (ac.signal.aborted) return
        if (!exhausted) {
          // MAX_PROBE_PAGES full pages without a short page: the catalog may
          // extend past what we fetched (e.g. exactly threshold rows with more
          // behind them) — only a short page proves exhaustion, so fall back
          // to server-filtered query mode rather than trust a partial cache.
          setMode('query')
          return
        }
        fullListRef.current = collected.sort(byName)
        setMode('full')
      } catch {
        if (!ac.signal.aborted) setMode('unavailable')
      }
    })()
  }, [active, config.enabled, config.serverLookupThreshold, feedId])

  const search = useCallback((query: string) => {
    const q = query.trim().toLowerCase()
    if (mode === 'full') {
      const list = fullListRef.current ?? []
      if (!q) {
        // Empty query stays SILENT in both modes (deliberate divergence from
        // the toolkit's full-mode "No matching property" flash, flagged as a
        // latent bug in the reference); showAllBeforeTyping opts into the list.
        setMatches(config.showAllBeforeTyping ? list : [])
        return
      }
      setMatches(list.filter((m) => m.name.toLowerCase().includes(q)))
      return
    }
    if (mode !== 'query') return
    if (!q) {
      abortRef.current?.abort()
      setMatches([])
      return
    }
    // No timer here: the caller already debounced the query by the configured
    // debounceMs — a second delay would make the saved setting inaccurate.
    ;(async () => {
      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      const seq = ++reqSeqRef.current
      try {
        const res = await fetch(propertyNamesUrl(feedId, { q, limit: String(PAGE_SIZE) }), { signal: ac.signal })
        if (!res.ok) throw new Error(`property-names ${res.status}`)
        const rows = parseRows(await res.json())
        if (rows === null) throw new Error('malformed')
        if (seq === reqSeqRef.current) setMatches([...rows].sort(byName))
      } catch {
        // Aborted/stale/failed lookups render nothing rather than an error.
        if (seq === reqSeqRef.current) setMatches([])
      }
    })()
  }, [mode, config.showAllBeforeTyping, feedId])

  useEffect(() => () => {
    abortRef.current?.abort()
    probeAbortRef.current?.abort()
  }, [])

  return {
    /** false until the probe succeeds — the "By name" UI must not render before/without it. */
    available: mode === 'full' || mode === 'query',
    /** Probe/lookup state — diagnostic surface so tests can observe the
     *  probing→unavailable/full/query transitions the UI deliberately hides. */
    mode,
    matches,
    search,
  }
}
